import React from "react";

import { faInfoCircle } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Icon, Spinner, Text, TextEditor } from "@performyard/py-components";
import { countTranscriptCharacters } from "@performyard/service-contracts";

import NotesDropdownHeader from "./NotesDropdownHeader";

import { useMeetingTranscript, useRbacObjectPermissions, useUpdateMeetingTranscript } from "../../../../api/hooks";
import useAutosave from "../../../../hooks/useAutosave";
import { useTranscriptState } from "../hooks";

import { config } from "../config";
import { useMeetingContext } from "../context";

type MeetingTranscriptSectionProps = {
	onDropdownSelect: (optionId: "my-notes" | "participant-notes" | "meeting-transcript") => void;
};

/**
 * Meeting transcript section with character limit and autosave.
 * Only creators can edit, participants can view read-only.
 */
const MeetingTranscriptSection = ({ onDropdownSelect }: MeetingTranscriptSectionProps): React.ReactElement => {
	const { meeting, userId } = useMeetingContext();
	const { data: transcriptData, isLoading: isTranscriptLoading } = useMeetingTranscript(meeting._id);
	const { mutate: updateMeetingTranscript, isError: isTranscriptError } = useUpdateMeetingTranscript();

	const { data: rbacPermissions } = useRbacObjectPermissions("meeting", meeting._id);
	const canEditTranscript = rbacPermissions?.includes("meetings:editTranscript") ?? false;

	const { transcriptContent, setTranscriptContent } = useTranscriptState({
		transcriptData,
		isTranscriptLoading,
	});

	const {
		showSaved: showTranscriptSaved,
		showSaving: showTranscriptSaving,
		handleChange: handleTranscriptChange,
	} = useAutosave({
		value: transcriptContent,
		setValue: setTranscriptContent,
		onSave: async (content) => {
			if (countTranscriptCharacters(content) <= config.maxTranscriptLength) {
				updateMeetingTranscript({
					meetingId: meeting._id,
					content: content,
				});
			}
		},
		delay: config.autosaveDelay,
		enabled: meeting.created.userId === userId,
		minSavingTime: config.minSavingTime,
	});

	return (
		<div className="flex flex-col gap-2">
			<NotesDropdownHeader
				selectedOption="meeting-transcript"
				showSaved={
					showTranscriptSaved && countTranscriptCharacters(transcriptContent) <= config.maxTranscriptLength
				}
				showSaving={
					showTranscriptSaving && countTranscriptCharacters(transcriptContent) <= config.maxTranscriptLength
				}
				onOptionSelect={onDropdownSelect}
				hasError={isTranscriptError}
			/>
			{isTranscriptLoading ? (
				<div className="flex items-center justify-center py-8">
					<Spinner size={config.spinnerSize.medium} />
				</div>
			) : (
				<>
					<TextEditor
						id="meeting-transcript-editor"
						initialValue={transcriptContent}
						onChange={handleTranscriptChange}
						placeholder="Your transcript here"
						readOnly={!canEditTranscript}
						characterLimit={config.maxTranscriptLength}
						minHeight={config.layout.textEditorMinHeight}
						maxHeight={config.layout.textEditorMaxHeight}
					/>
					{countTranscriptCharacters(transcriptContent) > config.maxTranscriptLength && (
						<div className="flex items-start gap-1" role="alert" aria-live="assertive">
							<Icon icon={faInfoCircle} size={16} color="red-5" aria-hidden="true" />
							<Text size="caption" color="red-5">
								Transcripts have a maximum length of {config.maxTranscriptLength.toLocaleString()}{" "}
								characters
							</Text>
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default MeetingTranscriptSection;
